import {
  Activity, RefreshCcw, Cpu, Gauge, BarChart3, Network, ShieldCheck, Zap, GitBranch,
  Bot, Workflow, Sparkles, Eye, Search, AlertTriangle, PlayCircle, Users, Lock,
  Layers, BrainCircuit, LineChart, Compass,
} from 'lucide-react';

// NOTE: These Unsplash IDs are intentionally distinct from the three used on
// the /platform (Overview) page (1650327034581-1711a15a5430, 1603054400223-7c796e60ba9d,
// 1617791160536-598cf32026fb). Each pillar gets its own visual set.

// Observability — dashboards, analytics, charts
const OBS_1 = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1400&q=80'; // laptop with graphs
const OBS_2 = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1400&q=80'; // analytics charts
const OBS_3 = 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1400&q=80'; // team dashboard

// Self-Healing — automation, robotics, resilience
const HEAL_1 = 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=1400&q=80'; // robot arm
const HEAL_2 = 'https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?w=1400&q=80'; // robotic hand
const HEAL_3 = 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1400&q=80'; // circuits / systems

// AI & Agents — AI brains, neural fabric, nodes
const AI_1 = 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1400&q=80'; // AI rendering
const AI_2 = 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1400&q=80'; // AI tech
const AI_3 = 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1400&q=80'; // matrix / data flow

export const platformPillars = {
  observability: {
    slug: 'observability',
    eyebrow: 'Observability',
    icon: Activity,
    accent: 'blue',
    title: ['End-to-end visibility,', 'in real time.'],
    lede: 'See every process, system, and decision as it happens. MetaxuOne ingests data from every layer of your enterprise — ERP, CRM, ITSM, custom apps — and assembles a single, living model you can trust.',
    clip: 'observability',
    capabilities: [
      { icon: Gauge,        title: 'Live process telemetry',  text: 'Continuous capture of activity, latency, throughput, and exceptions across every system.' },
      { icon: BarChart3,    title: 'KPI command center',      text: 'Trend, alert, and drill from board-level KPIs to the single transaction behind them.' },
      { icon: Network,      title: 'Cross-system lineage',    text: 'Trace any outcome back through every system, agent, and human that touched it.' },
      { icon: AlertTriangle,title: 'Anomaly detection',       text: 'Statistical + AI models surface drift and outliers the moment they appear.' },
      { icon: Search,       title: 'Drill-down explorer',     text: 'Open any spike or dip and explore the cohort of cases that caused it.' },
      { icon: Eye,          title: 'Always-on health',        text: 'A continuously updated map of process health across the enterprise.' },
    ],
    deepDives: [
      {
        title: 'A live picture of how work actually flows.',
        text:  'Process data is messy and scattered. MetaxuOne unifies it into one operational graph that updates as your business moves — no nightly ETL, no stale dashboards.',
        image: OBS_1,
        points: ['Real-time event ingestion','Pre-built connectors for 100+ systems','Native support for unstructured logs'],
      },
      {
        title: 'KPIs you can actually trust.',
        text:  'Every KPI is rooted in the underlying transactions. Click any number and see exactly which cases, users, and systems shaped it — no spreadsheet detective work.',
        image: OBS_2,
        reverse: true,
        points: ['Live KPI library','Lineage to source events','Configurable thresholds & alerts'],
      },
      {
        title: 'Designed for the people who run the work.',
        text:  'Role-aware views surface what each operator, analyst, and exec needs — and nothing else. Same source of truth, different lens.',
        image: OBS_3,
        points: ['Role-based dashboards','Saved investigations','Embed into existing tools'],
      },
    ],
    useCases: [
      { icon: LineChart, title: 'Order-to-cash latency', text: 'Pinpoint where days are lost between order entry and cash collection.' },
      { icon: Compass,   title: 'IT incident MTTR',     text: 'Trace incidents through every touchpoint to shrink mean time to resolution.' },
      { icon: Layers,    title: 'Supply chain drift',    text: 'See deviations in lead times the moment they form, not at end of quarter.' },
    ],
  },

  'self-healing': {
    slug: 'self-healing',
    eyebrow: 'Self-Healing',
    icon: RefreshCcw,
    accent: 'blue',
    title: ['Resilience that', 'runs itself.'],
    lede: "Don't just spot deviations — close them. The platform pairs detection with policy-driven remediation, so processes correct themselves before they impact the customer or the close.",
    clip: 'remediation-loop',
    capabilities: [
      { icon: ShieldCheck, title: 'Detect deviations in flight', text: 'Statistical and AI models pinpoint drift, bottlenecks, and outliers as they happen.' },
      { icon: Zap,         title: 'Trigger guided remediations', text: 'Run codified playbooks — automations, approvals, or human-in-the-loop tasks.' },
      { icon: GitBranch,   title: 'Closed-loop improvement',     text: 'Every healed event feeds the model so the next deviation is caught faster.' },
      { icon: PlayCircle,  title: 'Playbook library',            text: 'Compose, version, and reuse remediation playbooks across teams and regions.' },
      { icon: Users,       title: 'Human-in-the-loop',           text: 'Promote, pause, or override any automated action — full audit trail included.' },
      { icon: Activity,    title: 'MTTR reduction',              text: 'Cut mean time to recovery from hours to minutes by acting where issues are born.' },
    ],
    deepDives: [
      {
        title: 'Catch issues the moment they form.',
        text:  'Behavioural baselines and ML detectors run continuously against the live operational graph. The platform flags the deviation, the system that caused it, and the cohort it affects.',
        image: HEAL_1,
        points: ['Streaming detectors','Baselines per cohort','Severity-aware routing'],
      },
      {
        title: 'Remediate with codified playbooks.',
        text:  'Pre-built and custom playbooks orchestrate automations, approvals, and notifications. Pick the right response — human, agent, or both — based on policy.',
        image: HEAL_2,
        reverse: true,
        points: ['Low-code playbook builder','API + RPA + agent actions','Approval gating'],
      },
      {
        title: 'Learn from every recovery.',
        text:  'Each healed event becomes training signal — improving detection thresholds and playbook selection over time. The platform gets better the more it runs.',
        image: HEAL_3,
        points: ['Outcome tracking','Auto-tuned thresholds','Continuous feedback loop'],
      },
    ],
    useCases: [
      { icon: LineChart, title: 'Invoice exception recovery', text: 'Auto-retry, reroute, and escalate stuck invoices before they age past SLA.' },
      { icon: Compass,   title: 'Order rerouting',            text: 'Reroute around supplier delays before customer promise dates slip.' },
      { icon: Layers,    title: 'Ticket auto-triage',         text: 'Classify, enrich, and assign IT/HR tickets without a human in the middle.' },
    ],
  },

  'ai-agents': {
    slug: 'ai-agents',
    eyebrow: 'AI Agents',
    icon: Cpu,
    accent: 'blue',
    title: ['Agents grounded in', 'your operational truth.'],
    lede: "Generic AI doesn't know your business. MetaxuOne agents reason on top of the Context Model — your processes, your KPIs, your data — so they make decisions you can audit and stand behind.",
    clip: 'ai-agents',
    capabilities: [
      { icon: Bot,         title: 'Context-aware reasoning', text: 'Agents read the live process graph before they decide or act — no hallucinated state.' },
      { icon: Workflow,    title: 'Workflow orchestration',  text: 'Coordinate humans, agents, and existing systems against the same source of truth.' },
      { icon: Sparkles,    title: 'Continuous learning',     text: 'Outcomes flow back into the model — every decision sharpens the next.' },
      { icon: BrainCircuit,title: 'Multi-agent collaboration',text:'Specialised agents hand off work, peer-review actions, and escalate when blocked.'},
      { icon: Lock,        title: 'Audit & governance',      text: 'Every prompt, tool call, and decision is logged with full lineage for compliance.' },
      { icon: ShieldCheck, title: 'Safe deployment',         text: 'Sandboxed rollouts, action policies, and human approval rails by default.' },
    ],
    deepDives: [
      {
        title: 'Agents that understand your operation.',
        text:  'Every agent is grounded in the Context Model — they see real process state, KPIs, and history before they reason. No prompt gymnastics, no hallucinated workflows.',
        image: AI_1,
        points: ['Tools from your live graph','Grounded retrieval','Process-aware planning'],
      },
      {
        title: 'Orchestrate humans, agents, and systems.',
        text:  'Compose multi-step workflows where agents handle the rote, humans handle the judgement, and existing systems do what they were built for. One pane of glass.',
        image: AI_2,
        reverse: true,
        points: ['Visual workflow builder','Tool calling + approvals','Run-time observability'],
      },
      {
        title: 'Governed by design.',
        text:  'Granular action policies, audit trails, and rollback let you deploy agents in regulated environments without compromise.',
        image: AI_3,
        points: ['Action policies','Full decision lineage','Drift & quality monitors'],
      },
    ],
    useCases: [
      { icon: LineChart, title: 'Finance close acceleration', text: 'Agents reconcile, investigate, and post the routine — humans review exceptions.' },
      { icon: Compass,   title: 'Sales ops co-pilot',         text: 'Forecasts, hygiene fixes, and deal nudges grounded in pipeline reality.' },
      { icon: Layers,    title: 'IT ops automation',          text: 'Resolve incidents end-to-end with policy-bounded autonomous actions.' },
    ],
  },
};

export const pillarOrder = ['observability', 'self-healing', 'ai-agents'];
